// Import of the module

import { parseDate } from "@/lib/utils";
import i18n from "@/i18n";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";

// Type of the fields in the table

export type EtfStaticData = {
  id: number;
  benchmark_name?: string | null;
  replication_methodology?: number | null;
  is_funded?: boolean | null;
  is_leveraged?: boolean | null;
  is_ucits?: boolean | null;
  strategy_id: number;
  created_at: string;
  updated_at: string;
  open_fund_fields?: Record<string, unknown>;
  distribution_policy?: number | null;
  sfdr?: string | null;
};

// Definition of the columns

export const etf_static_data_columns: ColumnDef<EtfStaticData>[] = [
  {
    accessorKey: "id",
    header: "ID",
  },
  {
    accessorKey: "benchmark_name",
    header: "Benchmark Name",
    cell: ({ row }) => row.getValue("benchmark_name") || "-",
  },
  {
    accessorKey: "replication_methodology",
    header: "Replication Methodology",
    cell: ({ row }) => {
      const value = row.getValue("replication_methodology");
      if (value === null || value === undefined) return "-";
      // Map integer code to string
      return value === 0 ? "Physical" : "Synthetical";
    },
  },
  {
    accessorKey: "is_funded",
    header: "Funded",
    cell: ({ row }) => {
      const value = row.getValue("is_funded");
      if (value === null || value === undefined) return "-";
      return value ? "Yes" : "No";
    },
  },
  {
    accessorKey: "is_leveraged",
    header: "Leveraged",
    cell: ({ row }) => {
      const value = row.getValue("is_leveraged");
      if (value === null || value === undefined) return "-";
      return value ? "Yes" : "No";
    },
  },
  {
    accessorKey: "is_ucits",
    header: "UCITS",
    cell: ({ row }) => {
      const value = row.getValue("is_ucits");
      if (value === null || value === undefined) return "-";
      return value ? "Yes" : "No";
    },
  },
  {
    accessorKey: "strategy_id",
    header: "Strategy ID",
    cell: ({ row }) => row.getValue("strategy_id") || "-",
  },
  {
    accessorKey: "created_at",
    header: "Created At",
    cell: ({ row }) => parseDate(row.getValue("created_at")),
  },
  {
    accessorKey: "updated_at",
    header: "Updated At",
    cell: ({ row }) => parseDate(row.getValue("updated_at")),
  },
  {
    accessorKey: "open_fund_fields",
    header: "Open Fund Fields",
    size: 1200, // Set column width to 600px (2-3x larger)
    cell: ({ row }) => {
      const value = row.getValue("open_fund_fields");
      if (!value) return "-";
      return (
        <pre className="whitespace-pre-wrap text-xs max-w-2xl w-full overflow-auto">
          {JSON.stringify(value, null, 1)}
        </pre>
      );
    },
  },
  {
    accessorKey: "distribution_policy",
    header: "Distribution Policy",
    cell: ({ row }) => {
      const value = row.getValue("distribution_policy");
      if (value === null || value === undefined) return "-";
      // Map smallint code to string
      return value === 0 ? "Accumulating" : "Distributing";
    },
  },
  {
    accessorKey: "sfdr",
    header: "SFDR",
    cell: ({ row }) => row.getValue("sfdr") || "-",
  },
  {
    id: "actions",
    header: () => <div className="text-right">Actions</div>,
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue("id")}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      );
    },
  },
];
